import React, { useEffect, useState } from "react";
import Header from "../../common/Header";
import Sidebar from "../../common/Sidebar";
import PageHeader from "../PageHeader";
import { triggerAlert } from "../../utils/commonFunctions";
import Loader from "../../common/Loader";
import { AdminFreeTrials } from "../../utils/APIClient";

/**
 * Settings > Free Trials
 *
 * Master enable + per-category allowance for the marketplace "Start
 * free trial" button. Values are snapshotted onto each trial at grant
 * time — editing here only affects trials granted going forward, never
 * trials already in flight.
 *
 * One-trial-per-category-per-org, forever. Cancelling / expiring /
 * exhausting a trial still burns it for that category — customers get
 * exactly one shot to try each category.
 */

const CATEGORIES = [
  {
    key: "avatar",
    enabledField: "avatar_enabled",
    allowanceField: "avatar_units",
    durationField: "avatar_duration_days",
    label: "Avatar Chat Agents",
    // Customer-facing label. Backend field is avatar_units, but the
    // customer sees "minutes" — keep the admin label matching what the
    // customer reads so the two portals don't disagree.
    unit: "minutes",
    icon: "bi-mic-fill",
    help: "Session-minutes allowance granted to each avatar trial. Runs down as visitors chat.",
  },
  {
    key: "calling",
    enabledField: "calling_enabled",
    allowanceField: "calling_minutes",
    durationField: "calling_duration_days",
    label: "Calling Agents",
    unit: "minutes",
    icon: "bi-telephone-fill",
    help: "Call-minutes allowance for each calling-agent trial. Phone numbers are not included — customers still buy DIDs at real cost.",
  },
  {
    key: "workflow",
    enabledField: "workflow_enabled",
    allowanceField: "workflow_dollars",
    durationField: "workflow_duration_days",
    label: "AI Workflows",
    unit: "dollars",
    icon: "bi-diagram-3-fill",
    help: "Execution budget in dollars for each workflow trial. Runs down as workflow steps execute.",
  },
];

const DEFAULT_FORM = {
  enabled: false,
  avatar_enabled: false,
  avatar_units: "0",
  avatar_duration_days: 7,
  calling_enabled: false,
  calling_minutes: "0",
  calling_duration_days: 7,
  workflow_enabled: false,
  workflow_dollars: "0",
  workflow_duration_days: 7,
};

export default function FreeTrials() {
  document.title = "Free Trial Settings";
  const mainHeading = "Free Trials";
  const heading = "Settings / Free Trials";

  const [form, setForm] = useState(DEFAULT_FORM);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    AdminFreeTrials.get()
      .then((res) => {
        if (cancelled) return;
        const data = res.data || {};
        const next = { ...DEFAULT_FORM };
        Object.keys(DEFAULT_FORM).forEach((k) => {
          if (data[k] === undefined || data[k] === null) return;
          if (typeof DEFAULT_FORM[k] === "boolean") {
            next[k] = !!data[k];
          } else if (k.endsWith("_duration_days")) {
            next[k] = data[k] === "" ? "" : Number(data[k]);
          } else {
            next[k] = String(data[k]);
          }
        });
        setForm(next);
      })
      .catch(() => {
        triggerAlert("error", "Oops...", "Failed to load free-trial settings.");
      })
      .finally(() => !cancelled && setLoading(false));
    return () => {
      cancelled = true;
    };
  }, []);

  const update = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: e.target.value }));

  const updateBool = (key) => (e) =>
    setForm((prev) => ({ ...prev, [key]: !!e.target.checked }));

  const updateInt = (key) => (e) => {
    const v = e.target.value;
    setForm((prev) => ({ ...prev, [key]: v === "" ? "" : Number(v) }));
  };

  const handleSave = async (e) => {
    e?.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...form,
        // Coerce empty-string numeric inputs to sane defaults before send —
        // the backend expects real numbers, not "". Duration falls back to
        // 7 rather than 0 (a zero-day trial would expire the instant it
        // was granted).
        avatar_units: form.avatar_units === "" ? "0" : String(form.avatar_units),
        avatar_duration_days: form.avatar_duration_days === "" ? 7 : Number(form.avatar_duration_days),
        calling_minutes: form.calling_minutes === "" ? "0" : String(form.calling_minutes),
        calling_duration_days: form.calling_duration_days === "" ? 7 : Number(form.calling_duration_days),
        workflow_dollars: form.workflow_dollars === "" ? "0" : String(form.workflow_dollars),
        workflow_duration_days: form.workflow_duration_days === "" ? 7 : Number(form.workflow_duration_days),
      };
      const res = await AdminFreeTrials.update(payload);
      const changed = res.data?.updated_fields || [];
      if (changed.length) {
        triggerAlert(
          "success",
          "",
          `Saved ${changed.length} change${changed.length === 1 ? "" : "s"}. New trials will use the updated values; trials already in flight are unaffected.`
        );
      } else {
        triggerAlert("info", "", "No changes detected.");
      }
    } catch (err) {
      triggerAlert("error", "Oops...", "Failed to save free-trial settings.");
    } finally {
      setSaving(false);
    }
  };

  const masterOff = !form.enabled;

  return (
    <div id="layout-wrapper">
      <Header />
      <Sidebar />
      <div className="page-content" style={{ backgroundColor: "#ededed" }}>
        <div className="main-content">
          <PageHeader mainHeading={mainHeading} heading={heading} />
          <div className="container-fluid">
            {loading ? (
              <div className="loader-overlay text-white">
                <Loader />
              </div>
            ) : (
              <form onSubmit={handleSave}>
                {/* ── Global master switch ─────────────────────────── */}
                <Card title="Global" icon="bi-toggles">
                  <div className="row">
                    <div className="col-md-12">
                      <div className="mb-2">
                        <label className="form-label fw-semibold">Free trials enabled</label>
                        <div className="form-check form-switch">
                          <input
                            type="checkbox"
                            className="form-check-input"
                            checked={!!form.enabled}
                            onChange={updateBool("enabled")}
                            style={{ width: 44, height: 24 }}
                          />
                          <span className="ms-2 text-muted small">
                            Master switch. When off, the marketplace "Start free trial" button never appears in any category.
                          </span>
                        </div>
                        <small className="text-muted d-block mt-2">
                          Duration + allowance are set per category below — an admin can run e.g. a 7-day calling trial in parallel with a 15-day workflow trial.
                        </small>
                      </div>
                    </div>
                  </div>
                </Card>

                {/* ── Per-category blocks ─────────────────────────── */}
                {CATEGORIES.map((cat) => (
                  <Card
                    key={cat.key}
                    title={cat.label}
                    icon={cat.icon}
                    disabledBadge={
                      !form[cat.enabledField] || masterOff ? "Off" : null
                    }
                  >
                    <div className="row">
                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Enabled</label>
                          <div className="form-check form-switch">
                            <input
                              type="checkbox"
                              className="form-check-input"
                              checked={!!form[cat.enabledField]}
                              onChange={updateBool(cat.enabledField)}
                              disabled={masterOff}
                              style={{ width: 44, height: 24 }}
                            />
                            <span className="ms-2 text-muted small">
                              Show trial button for this category.
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">
                            Allowance per trial ({cat.unit})
                          </label>
                          <input
                            type="number"
                            min="0"
                            step="0.01"
                            className="form-control"
                            value={form[cat.allowanceField] ?? "0"}
                            onChange={update(cat.allowanceField)}
                            disabled={masterOff || !form[cat.enabledField]}
                            style={{ borderRadius: "8px", padding: "10px 14px", fontSize: "14px" }}
                          />
                          <small className="text-muted d-block mt-1">{cat.help}</small>
                        </div>
                      </div>
                      <div className="col-md-4">
                        <div className="mb-3">
                          <label className="form-label fw-semibold">Duration (days)</label>
                          <input
                            type="number"
                            min="1"
                            max="90"
                            className="form-control"
                            value={form[cat.durationField] ?? 7}
                            onChange={updateInt(cat.durationField)}
                            disabled={masterOff || !form[cat.enabledField]}
                            style={{ borderRadius: "8px", padding: "10px 14px", fontSize: "14px" }}
                          />
                          <small className="text-muted d-block mt-1">
                            On day N + 1, the saved card is auto-charged for 1 pack unless the customer cancelled first.
                          </small>
                        </div>
                      </div>
                    </div>
                  </Card>
                ))}

                {/* ── Save ────────────────────────────────────────── */}
                <div className="d-flex justify-content-end gap-2 mb-4">
                  <button
                    type="submit"
                    disabled={saving}
                    className="btn btn-primary"
                    style={{
                      backgroundColor: "#014670",
                      borderColor: "#014670",
                      padding: "10px 32px",
                      borderRadius: 8,
                      fontWeight: 600,
                    }}
                  >
                    {saving ? "Saving…" : "Save changes"}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// ── Presentational helpers (Bootstrap 5 primitives) ────────────────
function Card({ title, icon, disabledBadge, children }) {
  return (
    <div className="card border-0 shadow-sm mb-3">
      <div className="card-header bg-white border-bottom py-3 d-flex justify-content-between align-items-center">
        <h5 className="mb-0 fw-bold" style={{ color: "#014670" }}>
          {icon && <i className={`bi ${icon} me-2`} />}
          {title}
        </h5>
        {disabledBadge && (
          <span className="badge bg-secondary-subtle text-secondary">{disabledBadge}</span>
        )}
      </div>
      <div className="card-body p-4">{children}</div>
    </div>
  );
}
